/**
 * 位图导出（PNG / JPEG）。
 *
 * 四个必踩的坑，都在这里处理掉：
 *  1) **字体**：不等 `document.fonts.ready` 就光栅化，文字会回退成默认字体（中文尤其明显）；
 *  2) **外链资源**：`<image href="https://…">` 画进 canvas 会**污染画布** → `toBlob` 抛
 *     SecurityError。所以先把外链图片内联成 data URL；内联不了就**明确报错**，绝不出一张黑图；
 *  3) **JPEG 没有透明通道**：必须先铺背景色，否则透明区变黑；
 *  4) **尺寸**：canvas 有硬上限（边长 16384），超了会静默失败 → 夹取并回报给 UI。
 *
 * 为了 jsdom 里可测：光栅化依赖（loadImage / createCanvas / fontsReady / URL 操作）全部**注入**，
 * 纯逻辑（尺寸换算、文件名、外链检测、补 width/height）是不依赖浏览器渲染的部分。
 */
const NS_XLINK = 'http://www.w3.org/1999/xlink';

export type RasterFormat = 'png' | 'jpeg';

/** canvas 边长上限（Chrome/Safari 都是 16384；面积上限各家不同，取最保守的边长限制） */
export const MAX_RASTER_PX = 16384;

export interface RasterSizeInput {
  /** 内容尺寸（viewBox 宽高，用户单位） */
  width: number;
  height: number;
  /** 倍率（与 dpi 二选一） */
  scale?: number;
  /** 目标 dpi（给了 dpi 时必须同时给 mmWidth） */
  dpi?: number;
  /** 物理宽度（毫米） */
  mmWidth?: number;
  maxPx?: number;
}

export interface RasterPlan {
  pixelWidth: number;
  pixelHeight: number;
  /** 实际生效倍率（夹取后可能小于请求值） */
  scale: number;
  /** 是否因为超出 canvas 上限被夹取 */
  clamped: boolean;
  /** 目标 dpi（仅 dpi 模式有值） */
  dpi: number | null;
}

/**
 * 算出输出像素尺寸（纯函数）。
 *
 * dpi 模式的语义：`mmWidth` 毫米宽 × `dpi` → 像素宽（= mm/25.4×dpi），高度按内容比例推。
 * 这与倍率模式不是同一件事：倍率 = 相对 96dpi 的 CSS 像素放大。
 */
export function planRaster(input: RasterSizeInput): RasterPlan {
  const maxPx = input.maxPx ?? MAX_RASTER_PX;
  const w = Math.max(1, Number(input.width) || 0);
  const h = Math.max(1, Number(input.height) || 0);
  const useDpi = typeof input.dpi === 'number' && input.dpi > 0 && typeof input.mmWidth === 'number' && input.mmWidth > 0;

  let scale: number;
  let dpi: number | null = null;
  if (useDpi) {
    dpi = input.dpi as number;
    scale = ((input.mmWidth as number) / 25.4) * dpi / w;
  } else {
    scale = typeof input.scale === 'number' && input.scale > 0 ? input.scale : 1;
  }

  let pw = Math.round(w * scale);
  let ph = Math.round(h * scale);
  let clamped = false;
  const over = Math.max(pw, ph) / maxPx;
  if (over > 1) {
    scale = scale / over;
    pw = Math.round(w * scale);
    ph = Math.round(h * scale);
    clamped = true;
  }
  return { pixelWidth: Math.max(1, pw), pixelHeight: Math.max(1, ph), scale, clamped, dpi };
}

/** 源文件名 → 导出文件名主干：去 `.svg`、路径分隔符抹平（`a/b.svg` → `a_b`） */
export function fileBaseName(sourceName: string | null | undefined): string {
  return (sourceName ?? 'untitled.svg').replace(/\.svg$/i, '').replace(/[/\\]/g, '_') || 'untitled';
}

/** `figure.svg` + png + 2× → `figure@2x.png`；dpi 模式 → `figure-300dpi.png` */
export function rasterFileName(sourceName: string | null | undefined, plan: RasterPlan, format: RasterFormat): string {
  const base = fileBaseName(sourceName);
  const ext = format === 'jpeg' ? 'jpg' : 'png';
  const tag = plan.dpi ? `${Math.round(plan.dpi)}dpi` : `${Number(plan.scale.toFixed(3))}x`;
  return `${base}-${tag}.${ext}`;
}

/**
 * `<image>` 上的外链引用（非 data:/blob:）—— 会污染 canvas，必须先内联。
 * 返回去重后的 URL 列表。
 */
export function findExternalRefs(svgText: string): string[] {
  const out = new Set<string>();
  const re = /<image\b[^>]*?(?:xlink:)?href\s*=\s*["']([^"']+)["']/gi;
  for (const m of svgText.matchAll(re)) {
    const url = (m[1] ?? '').trim();
    if (!url || /^(data:|blob:)/i.test(url)) continue;
    out.add(url);
  }
  return [...out];
}

/**
 * 给根 `<svg>` 补上显式 `width`/`height`。
 *
 * 部分浏览器把 SVG 当图片加载时，若只有 viewBox 没有 width/height，会算成 0×0 → 光栅化空白。
 * 只动**内存里的副本**，不影响文档与导出（这条与"打开时不改文件"的无损策略一致）。
 */
export function withExplicitSize(
  svgText: string,
  width: number,
  height: number,
  io?: { parse(text: string): Document; serialize(doc: Document): string },
): string {
  if (!io && typeof DOMParser === 'undefined') return svgText;
  const parse = io?.parse ?? ((t: string) => new DOMParser().parseFromString(t, 'image/svg+xml'));
  const serialize = io?.serialize ?? ((d: Document) => new XMLSerializer().serializeToString(d));
  const doc = parse(svgText);
  const root = doc.documentElement;
  if (!root || root.tagName.toLowerCase() !== 'svg') return svgText;
  root.setAttribute('width', String(width));
  root.setAttribute('height', String(height));
  return serialize(root.ownerDocument ?? doc);
}

/* ------------------------------------------------------------------ 光栅化 */

/** canvas 最小接口（测试可注入假实现） */
export interface CanvasLike {
  width: number;
  height: number;
  getContext(kind: '2d'): Canvas2DLike | null;
  toBlob(cb: (blob: Blob | null) => void, type?: string, quality?: number): void;
}

export interface Canvas2DLike {
  fillStyle: string;
  fillRect(x: number, y: number, w: number, h: number): void;
  drawImage(source: CanvasImageSource, dx: number, dy: number, dw: number, dh: number): void;
}

export interface RasterDeps {
  /** SVG 文本 → 可绘制图片源（真实实现走 `<img src=blobURL>` + `decode()`） */
  loadImage(svgText: string, width: number, height: number): Promise<{ source: CanvasImageSource }>;
  createCanvas(width: number, height: number): CanvasLike;
  fontsReady(): Promise<void>;
  createUrl(blob: Blob): string;
  revokeUrl(url: string): void;
  /** 把外链图片内联成 data URL（真实实现走 fetch + FileReader）；失败抛错 */
  inlineExternal(url: string): Promise<string>;
  parse(svgText: string): Document;
  serialize(doc: Document): string;
}

export interface RasterOptions {
  format: RasterFormat;
  scale?: number;
  dpi?: number;
  mmWidth?: number;
  /** 背景色；`null`/省略 = 透明（PNG 默认透明；JPEG 默认白，因为它没有透明通道） */
  background?: string | null;
  /** JPEG 质量 0–1（默认 0.92） */
  quality?: number;
  maxPx?: number;
  deps?: Partial<RasterDeps>;
  /** 内容尺寸，不给就从 viewBox 读 */
  width?: number;
  height?: number;
}

export interface RasterResult {
  blob: Blob;
  plan: RasterPlan;
  /** 这个文档里的外链图片 URL（已尝试内联） */
  externalRefs: string[];
}

export class RasterError extends Error {}

/** 真实浏览器实现 */
export function browserRasterDeps(): RasterDeps {
  return {
    async loadImage(svgText, width, height) {
      const url = URL.createObjectURL(new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' }));
      try {
        const img = new Image();
        img.width = width;
        img.height = height;
        const ready = new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new RasterError('SVG 无法作为图片加载（可能含非法标记或外链资源）'));
        });
        img.src = url;
        await ready;
        // decode() 保证像素已可用（Safari 上 onload 后直接画会偶发空白）
        if (typeof img.decode === 'function') {
          try {
            await img.decode();
          } catch {
            /* decode 失败不致命，onload 已保证可绘制 */
          }
        }
        return { source: img };
      } finally {
        URL.revokeObjectURL(url);
      }
    },
    createCanvas: (width, height) => {
      const c = document.createElement('canvas');
      c.width = width;
      c.height = height;
      return c as unknown as CanvasLike;
    },
    fontsReady: async () => {
      const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
      if (fonts?.ready) await fonts.ready;
    },
    createUrl: (blob) => URL.createObjectURL(blob),
    revokeUrl: (url) => URL.revokeObjectURL(url),
    async inlineExternal(url) {
      const res = await fetch(url, { mode: 'cors' });
      if (!res.ok) throw new RasterError(`外链图片取不到（HTTP ${res.status}）：${url}`);
      const blob = await res.blob();
      return await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result));
        fr.onerror = () => reject(new RasterError(`外链图片转 data URL 失败：${url}`));
        fr.readAsDataURL(blob);
      });
    },
    parse: (svgText) => new DOMParser().parseFromString(svgText, 'image/svg+xml'),
    serialize: (doc) => new XMLSerializer().serializeToString(doc),
  };
}

/**
 * 把 SVG 文本光栅化成 PNG/JPEG。
 *
 * 外链图片：先尝试内联；内联失败就**抛 RasterError 并指出是哪些 URL** ——
 * 不内联的话 canvas 被污染，`toBlob` 会抛一个更难懂的安全错误，或者（更糟）出黑图。
 */
export async function rasterizeSvg(svgText: string, options: RasterOptions): Promise<RasterResult> {
  const deps: RasterDeps = { ...browserRasterDeps(), ...(options.deps ?? {}) };
  const size = options; // 尺寸从入参给（UI 已经知道 viewBox），避免在这里猜
  const plan = planRaster({
    width: size.width ?? 1,
    height: size.height ?? 1,
    scale: options.scale,
    dpi: options.dpi,
    mmWidth: options.mmWidth,
    maxPx: options.maxPx,
  });

  await deps.fontsReady();

  const externalRefs = findExternalRefs(svgText);
  let text = svgText;
  if (externalRefs.length > 0) {
    const doc = deps.parse(text);
    const root = doc.documentElement;
    if (!root) throw new RasterError('SVG 根元素缺失');
    for (const url of externalRefs) {
      let dataUrl: string;
      try {
        dataUrl = await deps.inlineExternal(url);
      } catch (err) {
        const why = err instanceof Error ? err.message : String(err);
        throw new RasterError(`画布里有外链图片，浏览器不允许把它画进 canvas（会污染画布）：${why}`);
      }
      for (const img of Array.from(root.querySelectorAll('image'))) {
        const href = img.getAttribute('href') ?? img.getAttributeNS(NS_XLINK, 'href') ?? img.getAttribute('xlink:href');
        if (href === url) {
          img.setAttribute('href', dataUrl);
          img.removeAttribute('xlink:href');
        }
      }
    }
    text = deps.serialize(doc);
  }

  // 显式 width/height（缺了会有浏览器把 SVG 图片按 0×0 处理 → 空白）
  text = withExplicitSize(text, size.width ?? plan.pixelWidth, size.height ?? plan.pixelHeight, {
    parse: deps.parse,
    serialize: deps.serialize,
  });

  const { source } = await deps.loadImage(text, plan.pixelWidth, plan.pixelHeight);
  const canvas = deps.createCanvas(plan.pixelWidth, plan.pixelHeight);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new RasterError('拿不到 2D 上下文，无法光栅化');

  const bg = options.background ?? (options.format === 'jpeg' ? '#ffffff' : null);
  if (bg) {
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, plan.pixelWidth, plan.pixelHeight);
  }
  ctx.drawImage(source, 0, 0, plan.pixelWidth, plan.pixelHeight);

  const mime = options.format === 'jpeg' ? 'image/jpeg' : 'image/png';
  const quality = options.format === 'jpeg' ? clamp01(options.quality ?? 0.92) : undefined;
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new RasterError('canvas 导出失败（可能被跨域资源污染或尺寸过大）'))),
      mime,
      quality,
    );
  });

  return { blob, plan, externalRefs };
}

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));
